import { useCallback, useState } from "react";
import Form from "react-bootstrap/Form";
import { Badge, Button } from "react-bootstrap";
import {
  GridForm,
  GridFormItemWrapper,
  FieldType,
  Toolbar,
} from "@bagel-web/components";
import { Field, Fields, FilterCriteria } from "./types";

const FilterForm = ({
  fields,
  criteria,
  onSetFilters,
}: {
  fields: Fields;
  criteria: FilterCriteria<string>;
  onSetFilters: (criteria: FilterCriteria<string>) => void;
}) => {
  const [localCriteria, setLocalCriteria] = useState<FilterCriteria<string>>(
    () => {
      const initCriteria = { ...criteria };
      return initCriteria;
    }
  );

  const handleFilterChange = useCallback(
    (field: Field) => (e: { target: { value: string } }) => {
      const updatedCriteria = { ...localCriteria };
      if (e.target.value && e.target.value !== "") {
        updatedCriteria[field.name] = { value: e.target.value };
      } else {
        delete updatedCriteria[field.name];
      }

      field.dependentFields?.forEach((dependentField) => {
        delete updatedCriteria[dependentField];
      });

      setLocalCriteria(updatedCriteria);
    },
    [localCriteria]
  );

  const handleFilterCheckChange = useCallback(
    (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedCriteria = { ...localCriteria };
      if (e.target.checked) {
        updatedCriteria[field.name] = { value: "true" };
      } else {
        delete updatedCriteria[field.name];
      }

      field.dependentFields?.forEach((dependentField) => {
        delete updatedCriteria[dependentField];
      });

      setLocalCriteria(updatedCriteria);
    },
    [localCriteria]
  );
  const handleApplyFilter = useCallback(() => {
    onSetFilters(localCriteria);
  }, [onSetFilters, localCriteria]);

  const handleClearFilter = useCallback(() => {
    setLocalCriteria({});
  }, []);

  const getFilterField = useCallback(
    (field: Field, autoFocus: boolean) => {
      if (field.Component) {
        return (
          <>
            <Form.Label column>{field.label}</Form.Label>
            <field.Component
              key={field.name}
              autoFocus={autoFocus}
              value={localCriteria[field.name]?.value || ""}
              onChange={handleFilterChange(field)}
              localCriteria={localCriteria}
            />
          </>
        );
      } else if (field.type === FieldType.DateTime) {
        return (
          <>
            <Form.Label column>{field.label}</Form.Label>
            <Form.Control
              key={field.name}
              required={field.required}
              autoFocus={autoFocus}
              type="datetime-local"
              value={localCriteria[field.name]?.value || ""}
              onChange={handleFilterChange(field)}
              step="1"
            />
          </>
        );
      } else if (field.type === FieldType.Date) {
        return (
          <>
            <Form.Label column>{field.label}</Form.Label>
            <Form.Control
              key={field.name}
              required={field.required}
              type="date"
              autoFocus={autoFocus}
              value={localCriteria[field.name]?.value || ""}
              onChange={handleFilterChange(field)}
            />
          </>
        );
      } else if (field.type === FieldType.Boolean) {
        return (
          <Form.Check
            key={field.name}
            required={field.required}
            autoFocus={autoFocus}
            checked={localCriteria[field.name]?.value === "true"}
            onChange={handleFilterCheckChange(field)}
            label={field.label}
          />
        );
      } else if (field.type === FieldType.Number) {
        return (
          <>
            <Form.Label column>{field.label}</Form.Label>
            <Form.Control
              key={field.name}
              required={field.required}
              type="number"
              min="0"
              autoFocus={autoFocus}
              value={localCriteria[field.name]?.value || ""}
              onChange={handleFilterChange(field)}
            />
          </>
        );
      } else {
        return (
          <>
            <Form.Label column>{field.label}</Form.Label>
            {(Array.isArray(field.hint) ? field.hint : [field.hint])
              .filter((hint): hint is string => Boolean(hint))
              .map((hint) => (
                <Badge
                  key={hint}
                  bg="secondary-subtle"
                  text="secondary-emphasis"
                  style={{
                    fontSize: "0.90em",
                    fontWeight: 400,
                    marginLeft: "0.25rem",
                  }}
                >
                  {hint}
                </Badge>
              ))}
            <Form.Control
              style={{
                fontSize: "0.875rem",
                ...(field.showMonoValue ? { fontFamily: "monospace" } : {}),
              }}
              key={field.name}
              required={field.required}
              autoFocus={autoFocus}
              type="search"
              value={localCriteria[field.name]?.value || ""}
              onChange={handleFilterChange(field)}
            />
          </>
        );
      }
    },
    [localCriteria, handleFilterChange]
  );

  const missingRequiredField = fields.some(
    (field) => field.required && !localCriteria[field.name]?.value
  );

  return (
    <>
      <GridForm autoFocus={true}>
        {fields
          .filter((field) => !field.hidden)
          .map((field, index) => (
            <GridFormItemWrapper colSpan={field.colSpan} key={field.name}>
              <Form.Group controlId={field.name}>
                {getFilterField(field, index === 0)}
              </Form.Group>
            </GridFormItemWrapper>
          ))}
      </GridForm>
      <Toolbar $justifyContent="end" className="mt-3">
        <Button variant="secondary" onClick={handleClearFilter}>
          Reset
        </Button>
        <Button disabled={missingRequiredField} onClick={handleApplyFilter}>
          Apply
        </Button>
      </Toolbar>
    </>
  );
};

export default FilterForm;
